import { useFeatureGate } from '@statsig/expo-bindings';
import { differenceInHours } from 'date-fns';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import { isWeb, UpcomingSessionWidget } from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../../common/utils/homeUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import BookSessionPrompt from '../../../guidanceSessions/components/BookSessionPrompt';
import { useGetNextGuidanceSession } from '../../../guidanceSessions/hooks/useGetNextGuidanceSession';
import { useMembershipGuidanceSessionInfo } from '../../../guidanceSessions/hooks/useMembershipGuidanceSessionInfo';
import { sessionForDetailsViewAtom } from '../../../guidanceSessions/state/GuidanceSessionsAtoms';
import { GuidanceSessionType } from '../../../guidanceSessions/types/GuidanceSessions';
import { ExpertAdviceCard } from './ExpertAdviceCard';

export const UPCOMING_SESSION_HOURS_THRESHOLD = 24 * 2;

const NextGuidanceSessionWidget = () => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const { trackViewOnce } = useViewEvents();
    const setSessionForDetailsViewAtom = useSetAtom(sessionForDetailsViewAtom);
    const {
        isLoading,
        nextGuidanceSession,
        nextGuidanceOnboardingSessionType,
        nextGuidanceOnboardingSessionNeedsBooking,
    } = useGetNextGuidanceSession();

    const needsAnyOnboardingSessionBooking =
        nextGuidanceOnboardingSessionNeedsBooking &&
        nextGuidanceOnboardingSessionType != null;

    const needsFirstOnboardingSessionBooking =
        needsAnyOnboardingSessionBooking &&
        nextGuidanceOnboardingSessionType === GuidanceSessionType.DISCOVERY;

    if (isLoading) {
        return null;
    }

    if (nextGuidanceSession) {
        const sessionDate = new Date(nextGuidanceSession.startTime);
        const isDiscoveryOrMoneyMapSession =
            nextGuidanceSession.type === GuidanceSessionType.DISCOVERY ||
            nextGuidanceSession.type === GuidanceSessionType.ROADMAP;
        if (
            !isDiscoveryOrMoneyMapSession &&
            differenceInHours(sessionDate, new Date()) >
                UPCOMING_SESSION_HOURS_THRESHOLD
        ) {
            return null;
        }

        const sessionDetails = getSessionDetails(nextGuidanceSession.type);

        // Analytics tracking
        trackViewOnce('home/UpcomingSessionCard');

        // Card press navigation handler
        const handleCardPress = () => {
            setSessionForDetailsViewAtom(nextGuidanceSession);
            router.navigate({
                pathname: '/guidance-sessions/session-details',
                params: {
                    guidanceSessionId: nextGuidanceSession.id,
                    doneRoute: '/home',
                },
            });
        };

        return (
            <Animated.View
                style={{ width: '100%' }}
                entering={FadeIn.duration(500)}
                layout={isWeb ? undefined : LinearTransition.duration(500)}
            >
                <UpcomingSessionWidget
                    title={sessionDetails.title}
                    session={{
                        startTime: nextGuidanceSession.startTime,
                        endTime: nextGuidanceSession.endTime,
                        videoConferenceUrl:
                            nextGuidanceSession.videoConferenceUrl,
                        attendeeTimezone: nextGuidanceSession.attendeeTimezone,
                    }}
                    guide={user?.membership?.guide}
                    onCardPress={handleCardPress}
                    maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
                    variant={goyoEnabled ? 'frosted' : 'card'}
                />
            </Animated.View>
        );
    }

    if (needsAnyOnboardingSessionBooking) {
        return (
            <Animated.View
                style={{ width: '100%' }}
                entering={FadeIn.duration(500)}
                layout={isWeb ? undefined : LinearTransition.duration(500)}
            >
                {goyoEnabled && needsFirstOnboardingSessionBooking ? (
                    <ExpertAdviceCard maxWidth={SIDEBAR_CONTENT_MAX_WIDTH} />
                ) : (
                    <BookSessionPrompt
                        sessionType={nextGuidanceOnboardingSessionType}
                        maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
                    />
                )}
            </Animated.View>
        );
    }

    return null;
};

export default NextGuidanceSessionWidget;
